import { useCallback, useEffect } from 'react';

import { LabeledSwitch } from '@/_components';
import { snackbar } from '@/_shared';

import { FilterApplyTrigger } from '../filters-apply-trigger';
import { getFilterKey, getFilterValue, useFiltersState } from '../helper';
import type { AdvanceFilter } from '../types';

export function BooleanFilter<T>({
  handleSubmitAdvFilters,
  ...perFilter
}: AdvanceFilter<T> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void;
}) {
  const { mainKey, subKey, subKey2, types, selectedType, filterValue, applied } = perFilter;
  const {
    fromState: [fromValue, setFromValue],
    toState: [toValue, setToValue],
    typeState: [type, setType],
    showTwoValues,
    transition: [isPending, startTransition],
  } = useFiltersState(filterValue ?? '', types, selectedType);

  const handleSubmit = useCallback(() => {
    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    let selectedType = { name: '', value: '' };
    if (type === 'true') {
      selectedType = types[0];
    }

    const key = getFilterKey(mainKey as string, subKey, subKey2);
    const { filter, filterValue } = getFilterValue(
      selectedType.value,
      key,
      showTwoValues ? [fromValue, toValue] : [fromValue]
    );

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: type === 'true',
        filterValue,
        valueToApply: filter,
        selectedType,
      });
    });
  }, [
    fromValue,
    toValue,
    showTwoValues,
    type,
    mainKey,
    subKey,
    subKey2,
    startTransition,
    handleSubmitAdvFilters,
    perFilter,
    types,
  ]);

  useEffect(() => {
    if (!applied) {
      setFromValue('');
      setToValue('');
      setType(null);
    } else {
      setType('true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  return (
    <section className="flex flex-col">
      <div className="mt-4 flex items-center space-x-2">
        <LabeledSwitch
          id={types[0].value}
          checked={type === 'true'}
          onCheckedChange={(checked) => setType(checked ? 'true' : 'false')}
          label={types[0].name}
          labelPlacement="before"
        />
      </div>
      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
